import { Link } from "react-router-dom";
import SEO from "../components/SEO";

/**
 * Editorial policy / standards page.
 *
 * Publishes the site's research methodology, sourcing standards,
 * corrections policy, and disclaimers. Referenced from every author
 * profile and from the site footer.
 */
export default function EditorialPolicy() {
  const url = "https://ohioaccidentaid.com/editorial-policy";
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Editorial Policy — Ohio Accident Aid",
    description:
      "How Ohio Accident Aid researches, sources, reviews, and corrects the information published on this site.",
    url,
    publisher: {
      "@type": "Organization",
      name: "Ohio Accident Aid",
      url: "https://ohioaccidentaid.com",
    },
  };

  return (
    <>
      <SEO
        title="Editorial Policy"
        description="How Ohio Accident Aid researches, sources, reviews, and corrects information — including our sourcing standard, corrections process, and disclaimers."
        url={url}
        schema={schema}
      />
      <section className="min-h-[60vh] px-6 py-16 md:py-24 bg-bone" data-testid="editorial-policy-page">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="text-sm font-semibold text-charcoal-soft hover:text-terracotta"
            data-testid="editorial-policy-back-home"
          >
            ← Back to home
          </Link>

          <h1
            className="mt-8 text-4xl md:text-6xl font-display font-bold text-forest tracking-tight leading-[1.05]"
            data-testid="editorial-policy-title"
          >
            Editorial policy
          </h1>
          <p className="mt-5 text-lg text-charcoal-soft leading-relaxed">
            This page explains how we research, source, review, and update
            information on Ohio Accident Aid. It applies to every article, guide,
            and local resource we publish.
          </p>

          <div className="mt-10 prose-content space-y-8" data-testid="editorial-policy-content">
            <section>
              <h2 className="text-2xl font-display font-bold text-forest mb-3">
                What Ohio Accident Aid is — and isn't
              </h2>
              <p className="text-charcoal-soft leading-relaxed">
                Ohio Accident Aid is an attorney-matching service that connects
                unrepresented Ohio accident victims with qualified attorneys. We
                are not a law firm, we do not practice law, and nothing on this
                site is legal advice. Content is provided to help readers
                understand their options and prepare useful questions before
                consulting a licensed Ohio attorney.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold text-forest mb-3">
                Sourcing standard
              </h2>
              <p className="text-charcoal-soft leading-relaxed">We prefer, in order:</p>
              <ol className="list-decimal ml-6 mt-3 space-y-2 text-charcoal-soft">
                <li>
                  Primary Ohio statutes — the{" "}
                  <a
                    href="https://codes.ohio.gov/ohio-revised-code"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-terracotta underline"
                    data-testid="policy-link-ohio-code"
                  >
                    Ohio Revised Code
                  </a>{" "}
                  and Ohio Administrative Code.
                </li>
                <li>
                  Published decisions of the Supreme Court of Ohio and Ohio
                  Courts of Appeals, cited by case name and reporter.
                </li>
                <li>
                  Federal statutes and regulations —{" "}
                  <a
                    href="https://www.ecfr.gov/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-terracotta underline"
                    data-testid="policy-link-ecfr"
                  >
                    the Code of Federal Regulations
                  </a>{" "}
                  and enabling U.S. Code sections.
                </li>
                <li>
                  Official state and federal agency publications — Ohio
                  Department of Public Safety and the Ohio State Highway Patrol,{" "}
                  <a
                    href="https://www.transportation.ohio.gov/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-terracotta underline"
                    data-testid="policy-link-odot"
                  >
                    ODOT
                  </a>
                  , FMCSA, and NHTSA.
                </li>
                <li>
                  Local municipal codes and county-court local rules, cited to
                  the code publisher.
                </li>
              </ol>
              <p className="text-charcoal-soft leading-relaxed mt-4">
                We do not use "studies show" language without naming a specific
                study we can link to. We do not fabricate case names, verdicts,
                settlement figures, crash counts, or traffic-volume statistics.
                Where a claim cannot be verified against a primary source, we
                either de-specify it or remove it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold text-forest mb-3">
                Authorship and review
              </h2>
              <p className="text-charcoal-soft leading-relaxed">
                Articles are authored under a named byline. The byline reflects
                who researched and drafted the article and who is accountable
                for its accuracy. It does not imply legal, medical, or
                investigative credentials unless a separate "Reviewed by" line
                names a credentialed professional.
              </p>
              <p className="text-charcoal-soft leading-relaxed mt-3">
                When a licensed Ohio attorney, physician, or other credentialed
                reviewer contributes a review, we name that reviewer and their
                actual credentials on the article. We do not attribute an
                author's byline as though the author were licensed to give legal
                or medical advice.
              </p>
              <p className="text-charcoal-soft leading-relaxed mt-3">
                See{" "}
                <Link
                  to="/authors/aaron-bankhurst"
                  className="text-terracotta underline"
                  data-testid="policy-link-author-profile"
                >
                  Aaron Bankhurst's author profile
                </Link>{" "}
                for his role at Ohio Accident Aid.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold text-forest mb-3">
                Updates and versioning
              </h2>
              <p className="text-charcoal-soft leading-relaxed">
                Every article shows the date it was published and, when
                applicable, the date of its most recent substantive edit. We
                update the "modified" date only when the underlying content has
                actually changed — for example, when a statute is amended, a
                case is decided, an agency data source is refreshed, or when we
                add a new section. Copy edits and typo fixes do not change the
                modified date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold text-forest mb-3">
                Corrections
              </h2>
              <p className="text-charcoal-soft leading-relaxed">
                If you believe an article contains a factual error, please{" "}
                <Link
                  to="/contact"
                  className="text-terracotta underline"
                  data-testid="policy-link-contact"
                >
                  contact us
                </Link>{" "}
                with the URL, the specific claim you're flagging, and — if
                available — the primary source that contradicts it. We aim to
                review reported errors within five business days and, where a
                correction is warranted, publish it with a dated note at the
                bottom of the affected article.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold text-forest mb-3">
                Not legal advice
              </h2>
              <p className="text-charcoal-soft leading-relaxed">
                Information on Ohio Accident Aid is general in nature and does
                not create an attorney-client relationship. Deadlines, insurance
                coverages, and liability rules can vary based on facts we don't
                know about your situation. Talk to a licensed Ohio attorney
                before relying on anything you read here.
              </p>
            </section>
          </div>
        </div>
      </section>
    </>
  );
}
